import { useState } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { useI18n } from '@/hooks/useI18n';
import { CreateOrphanItineraryPayload } from '@/hooks/useOrphanItineraries';

interface CreateOrphanItineraryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateOrphanItineraryPayload) => Promise<void>;
}

export const CreateOrphanItineraryModal = ({
  isOpen,
  onClose,
  onSubmit,
}: CreateOrphanItineraryModalProps) => {
  const { t } = useI18n();
  
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const resetForm = () => {
    setTitle('');
    setStartDate('');
    setEndDate('');
    setDescription('');
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = t('validation.titleRequired');
    }
    if (!startDate) {
      newErrors.startDate = t('validation.startDateRequired');
    }
    if (!endDate) {
      newErrors.endDate = t('validation.endDateRequired');
    }
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      newErrors.endDate = t('validation.endDateAfterStartDate');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload: CreateOrphanItineraryPayload = {
        title: title.trim(),
        start_date: startDate,
        end_date: endDate,
        budget: 0, // Default budget for orphan itineraries
        description: description.trim(),
      };

      await onSubmit(payload);
      handleClose();
    } catch (error) {
      // Error handled by onSubmit
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('playground.createItinerary')}
      size="md"
    >
      <div className="px-6 py-4 space-y-4">
        <div>
          <Label htmlFor="title">{t('playground.itineraryTitle')} *</Label>
          <Input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t('playground.itineraryTitlePlaceholder')}
            hasError={!!errors.title}
          />
          {errors.title && (
            <p className="text-sm text-red-500 mt-1">{errors.title}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="startDate">{t('itinerary.startDate')} *</Label>
            <DatePicker
              value={startDate}
              onSelect={setStartDate}
              placeholder="Select start date"
              hasError={!!errors.startDate}
              minDate={new Date()}
            />
            {errors.startDate && (
              <p className="text-sm text-red-500 mt-1">{errors.startDate}</p>
            )}
          </div>

          <div>
            <Label htmlFor="endDate">{t('itinerary.endDate')} *</Label>
            <DatePicker
              value={endDate}
              onSelect={setEndDate}
              placeholder="Select end date"
              hasError={!!errors.endDate}
              minDate={startDate ? new Date(startDate) : new Date()}
            />
            {errors.endDate && (
              <p className="text-sm text-red-500 mt-1">{errors.endDate}</p>
            )}
          </div>
        </div>

        <div>
          <Label htmlFor="description">{t('playground.description')}</Label>
          <Textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={t('playground.descriptionPlaceholder')}
            rows={4}
          />
        </div>
      </div>

      <div className="flex justify-end gap-2 m-6">
        <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? t('common.saving') : t('common.create')}
        </Button>
      </div>
    </Modal>
  );
};
